import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HistoryIcon from "@mui/icons-material/History";
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { CustomActionIconLink } from "../../../components/CustomActionIconLink/CustomActionIconLink";
import { DataTable, ColumnDef } from "../../../components/DataTable/DataTable";
import SelectedItemsPreview from "../../../components/SelectedItemsPreview/SelectedItemsPreview";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import {
  getAllUserAssignments,
  removeUserAssignments,
} from "../../../services/api/apiClient";
import { AssignmentStatus } from "../../../services/api/types/userAssignment/types";
import { Roles, UserStatusType } from "../../../services/api/types/users/users";
import { brand, gray } from "../../../theme/themePrimitives";
import { REMOVETEXT } from "../../../utils/constants";
import { formatDateOnly } from "../../../utils/helpers";
import {
  ViewDetailsItem,
  ViewDetailsModal,
} from "../../PortalConfiguration/Users/EditAttributes/components/ViewDetailsModal";
import { Filter } from "./Filter";
import { UserStatusModal } from "../../PortalConfiguration/Users/EditAttributes/components/UserStatusModal";
import { User } from "../../../services/api/types/general/user";
import { extractErrorMessage } from "../../../utils/errorUtils";

const MANUAL_SELECTED_USERS_KEY = "manualSelectedUsers";
const FILTERED_SELECTED_USERS_KEY = "filteredSelectedUsers";
const SELECTED_USERS_SOURCE_KEY = "selectedUsersSource";
const LEGACY_SELECTED_USERS_KEY = "selectedUsers";

function getSelectedUsersFromStorage(): User[] {
  try {
    const parseUsers = (key: string): User[] => {
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    };

    const source = localStorage.getItem(SELECTED_USERS_SOURCE_KEY) as
      | "manual"
      | "filtered"
      | null;

    const manualUsers = parseUsers(MANUAL_SELECTED_USERS_KEY);
    const filteredUsers = parseUsers(FILTERED_SELECTED_USERS_KEY);
    const legacyUsers = parseUsers(LEGACY_SELECTED_USERS_KEY);

    if (source === "filtered") {
      return filteredUsers.length ? filteredUsers : legacyUsers;
    }
    if (source === "manual") {
      return manualUsers;
    }
    if (filteredUsers.length) return filteredUsers;
    if (manualUsers.length) return manualUsers;
    return legacyUsers;
  } catch {
    return [];
  }
}

type AssignmentRow = {
  portalAssignmentUid: string;
  title: string;
  usersCount: number;
  group: string;
  dueDate: string;
  type: string;
  usersList?: any[];
  portalGroups?: Array<{ name: string }>;
  dueDates?: Array<{ dueDate: string }>;
  creationType?: string;
  users?: Array<{ UserPortalUid: string; name: string; email: string }>;
  dueDatesCount?: number;
  infoStatus?: string;
  code?: string;
  sku?: string;
  version?: string;
  versions?: Array<{ version: string }>;
  versionsCount?: number;
  assignmentStatus?: string;
};

const SUBGROUP_INDENT_PX = 3;

function expandGroupsToViewDetailsItems(
  groups: Array<{ name: string }>,
): ViewDetailsItem[] {
  const items: ViewDetailsItem[] = [];
  for (const group of groups) {
    const parts = group.name
      .split("/")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) continue;
    parts.forEach((part, i) => {
      items.push({ primary: part, indent: i * SUBGROUP_INDENT_PX });
    });
  }
  return items;
}

const ManageAssignments: React.FC = () => {
  const location = useLocation();
  const { portalUid } = useParams();
  const externalSelectedUsers: User[] = useMemo(() => {
    const fromState = (location.state &&
      (location.state as any).selectedUsers) as User[] | undefined;
    if (fromState && fromState.length > 0) {
      return fromState;
    }
    return getSelectedUsersFromStorage();
  }, [location.state]);
  const isExternalSelected = externalSelectedUsers.length > 0;

  const allSelectedUsersArchived =
    isExternalSelected &&
    externalSelectedUsers.every(
      (user) => user.status === UserStatusType.ARCHIVED,
    );
  const archivedUserUids = useMemo(
    () =>
      externalSelectedUsers
        .filter(
          (user) =>
            user.status === UserStatusType.ARCHIVED ||
            user.status === UserStatusType.ARCHIVED_PENDING,
        )
        .map((user) => user.UserPortalUid),
    [externalSelectedUsers],
  );

  const [localSelectedRows, setLocalSelectedRows] = useState<AssignmentRow[]>(
    [],
  );
  const [showFilter, setShowFilter] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAssignmentUsers, setSelectedAssignmentUsers] = useState<
    User[]
  >([]);
  const [showAssignmentUsers, setShowAssignmentUsers] = useState(false);
  const [currentAssignmentUid, setCurrentAssignmentUid] = useState<
    string | null
  >(null);
  const [currentAssignmentActive, setCurrentAssignmentActive] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState<Array<{ name: string }>>(
    [],
  );
  const [showGroups, setShowGroups] = useState(false);
  const [selectedDueDates, setSelectedDueDates] = useState<
    Array<{ dueDate: string }>
  >([]);
  const [showDueDates, setShowDueDates] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<
    Array<{ version: string }>
  >([]);
  const [showVersions, setShowVersions] = useState(false);
  const [rowStatuses, setRowStatuses] = useState<Record<string, boolean>>({});
  const assignmentsDataRef = useRef<Record<string, AssignmentRow>>({});
  const isAllSelectedRef = useRef<boolean>(false);
  const { auth } = useAuth();
  const isSystemAdmin = auth.user?.roles?.includes(Roles.SYSTEM_ADMIN);
  const isMainAdmin = auth.user?.roles?.includes(Roles.MAIN_ADMIN);
  const currentUser = auth.user?.UserPortalUid;
  const { showToast } = useToast();

  // Table state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState<string>("title");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filtersList, setFiltersList] = useState<
    {
      title: string;
      items: { id: string; name: string; checked?: boolean }[];
    }[]
  >([
    {
      title: "Status",
      items: [
        { id: "status-active", name: "Active", checked: true },
        { id: "status-inactive", name: "Inactive", checked: false },
      ],
    },
  ]);

  const [appliedFiltersList, setAppliedFiltersList] = useState<
    {
      title: string;
      items: { id: string; name: string; checked?: boolean }[];
    }[]
  >([
    {
      title: "Status",
      items: [
        { id: "status-active", name: "Active", checked: true },
        { id: "status-inactive", name: "Inactive", checked: false },
      ],
    },
  ]);

  const handleCheckboxChange = (groupIndex: number, itemIndex: number) => {
    setFiltersList((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[groupIndex].items[itemIndex].checked =
        !copy[groupIndex].items[itemIndex].checked;
      return copy;
    });
  };

  const clearFilters = () => {
    const clearedFilters = filtersList.map((g) => ({
      ...g,
      items: g.items.map((i) => ({ ...i, checked: false })),
    }));
    setFiltersList(clearedFilters);
    setAppliedFiltersList(clearedFilters);
    setLocalSelectedRows([]);
    setPage(1);
    isAllSelectedRef.current = false; // Reset "All" selection when filters change
  };

  const handleRemoveFilter = (groupId: string, itemId: string) => {
    const groupIndex = appliedFiltersList.findIndex((g) => g.title === groupId);
    if (groupIndex === -1) return;

    const itemIndex = appliedFiltersList[groupIndex].items.findIndex(
      (it) => it.id === itemId,
    );
    if (itemIndex === -1) return;

    const updatedApplied = JSON.parse(JSON.stringify(appliedFiltersList));
    updatedApplied[groupIndex].items[itemIndex].checked = false;
    setAppliedFiltersList(updatedApplied);

    const updatedPending = JSON.parse(JSON.stringify(filtersList));
    updatedPending[groupIndex].items[itemIndex].checked = false;
    setFiltersList(updatedPending);
  };

  const appliedFilters = useMemo(() => {
    return appliedFiltersList
      .flatMap((g) => g.items.map((i) => ({ group: g.title, ...i })))
      .filter((i) => i.checked);
  }, [appliedFiltersList]);

  const [refreshKey, setRefreshKey] = useState(0);

  const statusFilter = useMemo(() => {
    const statusItems = appliedFiltersList
      .find((f) => f.title === "Status")
      ?.items.filter((item) => item.checked);

    if (!statusItems || statusItems.length === 0) return undefined;
    if (statusItems.length === 2) return undefined;

    if (statusItems.some((item) => item.id === "status-active")) {
      return AssignmentStatus.ACTIVE;
    }
    if (statusItems.some((item) => item.id === "status-inactive")) {
      return AssignmentStatus.INACTIVE;
    }
    return undefined;
  }, [appliedFiltersList]);

  const fetchAssignments = useCallback(async () => {
    if (!portalUid) {
      return;
    }

    setLoading(true);
    try {
      const userIds = isExternalSelected
        ? externalSelectedUsers.map((user) => user.UserPortalUid)
        : [];

      const queryParams: {
        limit?: number;
        offset?: number;
        search?: string;
        status?: AssignmentStatus;
        orderBy?: string;
        sort?: string;
      } = {};

      // Only include pagination params if "All" is not selected
      // We use a ref to track "All" selection to avoid adding totalCount to dependencies
      if (!isAllSelectedRef.current) {
        queryParams.limit = limit;
        queryParams.offset = (page - 1) * limit;
      }

      if (searchTerm) {
        queryParams.search = searchTerm;
      }

      if (statusFilter) {
        queryParams.status = statusFilter;
      }

      if (orderBy) {
        queryParams.orderBy = orderBy;
        queryParams.sort = sortOrder;
      }

      const response = await getAllUserAssignments(portalUid, {
        users: userIds,
        queryParams,
      });

      void refreshKey;

      const formattedAssignments: AssignmentRow[] = response.rows.map(
        (assignment: any) => {
          const group = assignment.portalGroups?.[0]?.name || "No Group";
          const rawDueDate = assignment.dueDates?.[0]?.dueDate || "No due date";
          const dueDate = formatDateOnly(rawDueDate);

          const assignmentId =
            assignment.portalAssignmentUid || assignment.title;

          const row: AssignmentRow = {
            portalAssignmentUid: assignmentId,
            title: assignment.title,
            usersCount: assignment.usersCount || 0,
            group,
            dueDate,
            type: assignment.creationType || "Assignment",
            usersList:
              assignment.users?.map((user: User) => ({
                UserPortalUid:
                  user.UserPortalUid || (user as any).userPortalUid,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                status: user.status,
                dueDate: user.dueDate
                  ? formatDateOnly(user.dueDate)
                  : undefined,
              })) || [],
            portalGroups: assignment.portalGroups,
            dueDates: assignment.dueDates,
            dueDatesCount: assignment.dueDatesCount,
            creationType: assignment.creationType,
            users: assignment.users,
            infoStatus: assignment.infoStatus,
            code: assignment.code || "",
            sku: assignment.SKU || assignment.sku || "",
            version: (assignment.versions || [])
              .map((v: { version: string }) => v.version)
              .filter(Boolean)
              .join(", "),
            versions: assignment.versions || [],
            versionsCount:
              assignment.versionsCount ?? (assignment.versions || []).length,
            assignmentStatus: assignment.assignmentStatus,
          };

          assignmentsDataRef.current[assignmentId] = row;

          setRowStatuses((prev) => {
            if (!(assignmentId in prev)) {
              return { ...prev, [assignmentId]: true };
            }
            return prev;
          });

          return row;
        },
      );

      setRows(formattedAssignments);
      const newTotalCount = response.count || 0;
      setTotalCount(newTotalCount);
      // Update the "All" selection ref based on current limit and new totalCount
      isAllSelectedRef.current = limit === newTotalCount && newTotalCount > 0;
    } catch (error) {
      showToast(
        extractErrorMessage(error, "Failed to fetch assignments"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [
    portalUid,
    isExternalSelected,
    externalSelectedUsers,
    statusFilter,
    refreshKey,
    page,
    limit,
    searchTerm,
    orderBy,
    sortOrder,
    showToast,
  ]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const StyledLink = styled(RouterLink)(({ theme }) => ({
    color: "white",
    backgroundColor: brand[600],
    padding: theme.spacing(1, 2),
    textDecoration: "none",
    borderRadius: "12px",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: brand[700],
    },
  }));

  const selectedUsers: User[] = useMemo(() => {
    if (isExternalSelected) {
      return externalSelectedUsers;
    }

    const allUsers = localSelectedRows.flatMap((assignment) => {
      return (assignment.usersList || []).map((user) => ({
        UserPortalUid: user.UserPortalUid,
        name: user.name,
        email: user.email,
        createdAt: user.createdAt,
        dueDate: user.dueDate,
      }));
    });

    const uniqueUsersMap = new Map<string, User>();
    allUsers.forEach((user) => {
      if (!uniqueUsersMap.has(user.UserPortalUid)) {
        // @ts-ignore
        uniqueUsersMap.set(user.UserPortalUid, user);
      }
    });

    return Array.from(uniqueUsersMap.values());
  }, [isExternalSelected, externalSelectedUsers, localSelectedRows]);

  const saveFilters = () => {
    setAppliedFiltersList(JSON.parse(JSON.stringify(filtersList)));
    setShowFilter(false);
    isAllSelectedRef.current = false; // Reset "All" selection when filters are applied
  };

  const base = `/portal-configuration/${portalUid}`;
  const firstSelectedAssignment =
    localSelectedRows.length > 0 ? localSelectedRows[0] : null;
  const allSelectedInactive =
    localSelectedRows.length > 0 &&
    localSelectedRows.every(
      (row) => row.infoStatus === AssignmentStatus.INACTIVE,
    );
  const hasInactiveSelected = localSelectedRows.some(
    (row) =>
      row.infoStatus === AssignmentStatus.INACTIVE || row.infoStatus === "MIX",
  );

  const actions = useMemo(() => {
    const selectedAssignments =
      localSelectedRows.length > 0
        ? localSelectedRows.map((assignment) => ({
            id: assignment.portalAssignmentUid,
            title: assignment.title,
            metadata: {
              group: assignment.group,
              dueDate: assignment.dueDate,
              type: assignment.type,
            },
          }))
        : [];

    return [
      {
        to: firstSelectedAssignment
          ? `${base}/users/assignments/${firstSelectedAssignment.portalAssignmentUid}/edit`
          : "#",
        title: "Edit",
        icon: <DriveFileRenameOutlineOutlinedIcon />,
        disabled: !firstSelectedAssignment || allSelectedInactive,
        state: firstSelectedAssignment
          ? {
              assignmentData: {
                id: firstSelectedAssignment.portalAssignmentUid,
                title: firstSelectedAssignment.title,
                group: firstSelectedAssignment.group,
                dueDate: firstSelectedAssignment.dueDates?.[0]?.dueDate || "",
                status:
                  rowStatuses[firstSelectedAssignment.portalAssignmentUid] ??
                  true,
                usersList: firstSelectedAssignment.usersList || [],
              },
              selectedUsers: isExternalSelected
                ? externalSelectedUsers
                : selectedUsers,
              selectedAssignments: selectedAssignments,
              hasInactiveSelected,
            }
          : {},
        hidden: localSelectedRows.length === 0,
      },
      {
        to: "#",
        title: REMOVETEXT,
        icon: <DeleteOutlineIcon />,
        onClick: () => setDeleteModalOpen(true),
        disabled: localSelectedRows.length === 0,
        hidden:
          localSelectedRows.length === 0 || !(isMainAdmin || isSystemAdmin),
      },
    ];
  }, [
    firstSelectedAssignment,
    base,
    isExternalSelected,
    externalSelectedUsers,
    selectedUsers,
    rowStatuses,
    localSelectedRows,
    isMainAdmin,
    isSystemAdmin,
    allSelectedInactive,
    hasInactiveSelected,
  ]);

  // Table handlers
  const handleSortChange = (
    newOrderBy: string,
    newSortOrder: "asc" | "desc",
  ) => {
    setOrderBy(newOrderBy);
    setSortOrder(newSortOrder);
    setPage(1);
    isAllSelectedRef.current = false; // Reset "All" selection when sorting changes
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value === searchTerm) return;
      setSearchTerm(value);
      setPage(1);
      isAllSelectedRef.current = false; // Reset "All" selection when search changes
    },
    [searchTerm],
  );

  const handleRowsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    // Check if "All" is selected by comparing with current totalCount
    // When "All" is selected, newLimit equals totalCount
    isAllSelectedRef.current = newLimit === totalCount && totalCount > 0;
  };

  const handleSelectionChange = (selected: AssignmentRow[]) => {
    setLocalSelectedRows(selected);
  };

  // Column definitions
  const columns: ColumnDef<AssignmentRow>[] = useMemo(
    () => [
      {
        id: "title",
        label: "Title",
        sortable: true,
        getValue: (row) => row.title,
      },
      {
        id: "users",
        label: "Users",
        render: (row) => {
          const assignment =
            assignmentsDataRef.current[row.portalAssignmentUid];
          return (
            <Link
              component="button"
              underline="hover"
              sx={{ color: "#7C3AED", fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                if (assignment) {
                  setSelectedAssignmentUsers(assignment.usersList || []);
                  setCurrentAssignmentUid(assignment.portalAssignmentUid);
                  setCurrentAssignmentActive(
                    assignment.assignmentStatus !== AssignmentStatus.INACTIVE,
                  );
                  setShowAssignmentUsers(true);
                }
              }}
            >
              {row.usersCount}
            </Link>
          );
        },
      },
      {
        id: "infoStatus",
        label: "Status",
        getValue: (row) => row.infoStatus || "",
      },
      {
        id: "portalGroups",
        label: "Group/Subgroup",
        sortable: true,
        render: (row) => {
          const assignment =
            assignmentsDataRef.current[row.portalAssignmentUid];
          const groupsCount = row.portalGroups?.length || 0;
          return (
            <Link
              component="button"
              underline="hover"
              sx={{ color: "#7C3AED", fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                if (assignment?.portalGroups) {
                  setSelectedGroups(assignment.portalGroups);
                  setShowGroups(true);
                }
              }}
            >
              {groupsCount}
            </Link>
          );
        },
      },
      {
        id: "dueDate",
        label: "Due date",
        render: (row) => {
          const assignment =
            assignmentsDataRef.current[row.portalAssignmentUid];
          return (
            <Link
              component="button"
              underline="hover"
              sx={{ color: "#7C3AED", fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                if (assignment) {
                  setSelectedDueDates(assignment.dueDates || []);
                  setShowDueDates(true);
                }
              }}
            >
              {row.dueDatesCount || 0}
            </Link>
          );
        },
      },
      {
        id: "type",
        label: "Assignment type",
        getValue: (row) => row.type,
      },
      {
        id: "code",
        label: "Code",
        maxWidth: 100,
        getValue: (row) => row.code || "",
      },
      {
        id: "sku",
        label: "SKU",
        maxWidth: 100,
        getValue: (row) => row.sku || "",
      },
      {
        id: "version",
        label: "Version",
        maxWidth: 100,
        render: (row) => {
          const assignment =
            assignmentsDataRef.current[row.portalAssignmentUid];
          return (
            <Link
              component="button"
              underline="hover"
              sx={{ color: "#7C3AED", fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                if (assignment) {
                  setSelectedVersions(assignment.versions || []);
                  setShowVersions(true);
                }
              }}
            >
              {row.versionsCount || 0}
            </Link>
          );
        },
      },
    ],
    [],
  );

  usePageMetadata(
    {
      title: "Manage user assignments",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Manage assignments",
        },
      ],
    },
    [portalUid],
  );

  const handleUsersUpdated = useCallback(
    (updatedUsers: User[]) => {
      setSelectedAssignmentUsers(updatedUsers);

      // Update assignment data ref
      if (
        currentAssignmentUid &&
        assignmentsDataRef.current[currentAssignmentUid]
      ) {
        const assignment = assignmentsDataRef.current[currentAssignmentUid];
        if (assignment.usersList) {
          assignment.usersList = assignment.usersList.map((user: any) => {
            const uid = user.UserPortalUid || user.userPortalUid;
            const updatedUser = updatedUsers.find(
              (u) => u.UserPortalUid === uid,
            );
            return updatedUser ? { ...user, status: updatedUser.status } : user;
          });
        }
      }
    },
    [currentAssignmentUid],
  );

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <>
      <SelectedItemsPreview
        items={selectedUsers.map((user) => ({
          id: user.UserPortalUid,
          title: `${user.firstName} ${user.lastName}`,
          subtitle: user.email,
        }))}
        itemType="users"
        previewTitle={`${selectedUsers.length} ${
          selectedUsers.length === 1 ? "User" : "Users"
        } Selected`}
        previewDescription="View and manage the users this change will affect"
      />

      <DataTable
        data={rows}
        columns={columns}
        totalCount={totalCount}
        page={page}
        rowsPerPage={limit}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        sort={{ orderBy, order: sortOrder }}
        onSortChange={handleSortChange}
        search={searchTerm}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search by title, code or type"
        isLoading={loading}
        enableSelection={true}
        selectedRows={localSelectedRows}
        onSelectionChange={handleSelectionChange}
        getRowId={(row) => row.portalAssignmentUid}
        getCellSx={() => ({ py: 0.5 })}
        selectedCountTitle="Selected assignments"
        actions={
          <Stack direction="row" spacing={1}>
            {actions.map((a) => (
              <CustomActionIconLink
                key={a.title}
                to={a.to}
                state={a.state || { selectedUsers }}
                title={a.title}
                icon={a.icon}
                onClick={a.onClick}
                disabled={a.disabled}
                hidden={a.hidden}
              />
            ))}
            <Divider
              orientation="vertical"
              flexItem
              hidden={localSelectedRows.length === 0}
            />
            {allSelectedUsersArchived ? (
              <Tooltip title="Cannot add new assignments to archived users">
                <span>
                  <Button
                    disabled
                    sx={{
                      borderRadius: "12px",
                      padding: "8px 16px",
                      fontWeight: "bold",
                      "&.Mui-disabled": {
                        border: `1px solid ${gray[300]}`,
                        backgroundColor: gray[100],
                        color: gray[400],
                      },
                    }}
                  >
                    Add new assignment
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <StyledLink
                to={`${base}/users/assignments/add`}
                state={{
                  selectedUsers: isExternalSelected
                    ? externalSelectedUsers
                    : localSelectedRows
                        .flatMap((assignment) => assignment.usersList || [])
                        .map((user) => ({
                          UserPortalUid: user.UserPortalUid,
                          name: user.name,
                          email: user.email,
                          dueDate: user.dueDate,
                        })),
                }}
              >
                Add new assignment
              </StyledLink>
            )}
            <Button
              id="manage-assignments-filter-button"
              sx={{
                borderRadius: "12px",
                padding: "16px",
                border: `1px solid ${gray[300]}`,
              }}
              startIcon={
                <FilterListIcon
                  sx={{
                    color: gray[700],
                  }}
                />
              }
              onClick={() => {
                setShowFilter(!showFilter);
              }}
            >
              Filter
            </Button>
          </Stack>
        }
        headerContent={
          appliedFilters.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 1,
                paddingBottom: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography>Filter applied:</Typography>
                {appliedFilters.map((f) => (
                  <Chip
                    key={f.id}
                    label={`${f.group}: ${f.name}`}
                    size="medium"
                    variant="outlined"
                    onDelete={() => handleRemoveFilter(f.group, f.id)}
                  />
                ))}
              </Box>
              <Button size="small" onClick={() => clearFilters()}>
                Clear all
              </Button>
            </Box>
          ) : undefined
        }
      />

      <Filter
        filtersList={filtersList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        handleCheckboxChange={handleCheckboxChange}
        saveFilters={saveFilters}
        clearFilters={clearFilters}
      />

      <UserStatusModal
        setOpenUserStatusModal={setShowAssignmentUsers}
        openUserStatusModal={showAssignmentUsers}
        users={selectedAssignmentUsers}
        portalUid={portalUid}
        assignmentUid={currentAssignmentUid || undefined}
        assignmentActive={currentAssignmentActive}
        isUserArchived={allSelectedUsersArchived}
        archivedUserUids={archivedUserUids}
        onUsersUpdated={handleUsersUpdated}
        onRefresh={handleRefresh}
        emptyMessage="No users available"
      />

      <ViewDetailsModal
        setOpenViewDetailsModal={setShowGroups}
        openViewDetailsModal={showGroups}
        title="Groups/Subgroups"
        items={expandGroupsToViewDetailsItems(selectedGroups)}
        emptyMessage="No groups/subgroups"
        icon={<FolderOutlinedIcon sx={{ height: "24px" }} />}
      />

      <ViewDetailsModal
        setOpenViewDetailsModal={setShowDueDates}
        openViewDetailsModal={showDueDates}
        title="Due dates"
        items={
          selectedDueDates.map((dueDate) => ({
            primary: formatDateOnly(dueDate.dueDate),
          })) as ViewDetailsItem[]
        }
        emptyMessage="No deadline"
        icon={<CalendarTodayIcon sx={{ height: "24px" }} />}
      />

      <ViewDetailsModal
        setOpenViewDetailsModal={setShowVersions}
        openViewDetailsModal={showVersions}
        title="Versions"
        items={
          selectedVersions.map((version) => ({
            primary: version.version || "No version linked yet",
            emptyState: !version.version,
          })) as ViewDetailsItem[]
        }
        emptyMessage="No versions"
        icon={<HistoryIcon sx={{ height: "24px" }} />}
      />

      <ConfirmModal
        openConfirmModal={deleteModalOpen}
        setOpenConfirmModal={setDeleteModalOpen}
        modalTitle="Remove assignment"
        modalSubtitle="Are you sure you want to proceed? The user assignments and all related interaction data will be permanently deleted and cannot be recovered."
        modalLoading={isDeleting}
        handleModalConfirm={async () => {
          if (
            !portalUid ||
            localSelectedRows.length === 0 ||
            !(isSystemAdmin || isMainAdmin)
          )
            return;

          setIsDeleting(true);
          try {
            const userIds = isExternalSelected
              ? externalSelectedUsers.map((user) => user.UserPortalUid)
              : [];

            await removeUserAssignments(portalUid, {
              assignments: localSelectedRows.map((assignment) => ({
                portalAssignmentUid: assignment.portalAssignmentUid,
                users: userIds.length > 0 ? userIds : [],
              })),
              adminUser: currentUser || "",
            });

            setRefreshKey((prev) => prev + 1);
            setLocalSelectedRows([]);
            setDeleteModalOpen(false);
            showToast("Assignments removed successfully", "success");
          } catch (error) {
            showToast(
              extractErrorMessage(error, "Failed to fetch assignments"),
              "error",
            );
          } finally {
            setIsDeleting(false);
          }
        }}
        confirmButtonClick={() => {}}
      />
    </>
  );
};

export default ManageAssignments;
